import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  useGetBuyerInventories,
  useGetUserPreferences,
  useUpdateUserPreferences,
  useUpsertBuyerInventoryConfiguration,
  useUpsertInventory,
} from '~/api';
import type { BuyerInventoryQueryParams } from '~/api';
import { Modal } from '~/components/overlays';
import {
  applyColumnOrder,
  Table,
  useColumnOrderPreference,
  useTable,
} from '~/components/ui/table';
import type {
  BuyerInventoryFiltersInput,
  BuyerInventoryQuery,
  Inventory,
  UpsertBuyerInventoryConfigurationMutation,
} from '~/graphql/graphql';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useRouteSort } from '~/hooks/useRouteSort';
import { AddToBuyListModal } from './AddToBuyListModal';
import { EditInventoryModal } from './EditInventoryModal';
import type { PaginatedResponse } from '~/types';
import { setInventories } from '~/redux/reducers/inventory';
import { useDispatch } from 'react-redux';
import { buildFlatColumns, buildInventoryRow } from './inventoryRows';

// UI column key -> backend sort-field whitelist entry (BuyerInventorySortJpql). A column
// absent from this map is not server-sortable.
const SORT_FIELDS: Partial<Record<string, string>> = {
  productCode: 'productCode',
  product: 'product',
  length: 'length',
  location: 'location',
  locationCode: 'locationCode',
  stock: 'available',
  onOrder: 'onOrder',
  requested: 'requested',
  inbound: 'inbound',
  weeksCovered: 'weeksCovered',
  usageRatePerWeek: 'usageRatePerWeek',
};

type InventoryTableProps = {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly filters?: BuyerInventoryFiltersInput;
  readonly includeInventoryLengths: boolean;
};

export function InventoryTable({
  t,
  filters,
  includeInventoryLengths,
}: Readonly<InventoryTableProps>) {
  const [showEditInventory, setShowEditInventory] = useState(false);
  const [editingInventory, setEditingInventory] = useState<Inventory | null>(
    null
  );
  const [addToBuyListInventory, setAddToBuyListInventory] =
    useState<Inventory | null>(null);
  const { sort, onSort } = useRouteSort(SORT_FIELDS);

  const queryParams = useMemo<BuyerInventoryQueryParams>(
    () => ({ filters, includeInventoryLengths }),
    [filters, includeInventoryLengths]
  );

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const upsertInventory = useUpsertInventory();
  const upsertInventoryConfiguration = useUpsertBuyerInventoryConfiguration();
  const dispatch = useDispatch();

  const {
    data: allInventories,
    sentinelRef,
    isLoadingMore,
    isLoading,
    hasMoreData,
    error,
    updateItem,
    pagination,
  } = usePaginatedQuery<
    BuyerInventoryQuery,
    Inventory,
    BuyerInventoryQueryParams
  >({
    fetchHook: useGetBuyerInventories,
    select: (data) => data?.buyerInventory as PaginatedResponse<Inventory>,
    // catchUpOnSortChange: re-sorting must not throw away the rows the user has already
    // scrolled to — the list reloads to the same depth in the new order.
    options: { filters: queryParams, sort, catchUpOnSortChange: true },
  });

  // Feeds the CSV with-data export; unmount-clear stops a stale flat page leaking into it.
  useEffect(() => {
    dispatch(setInventories(allInventories));
  }, [allInventories, dispatch]);
  useEffect(() => {
    return () => {
      dispatch(setInventories([]));
    };
  }, [dispatch]);

  const handleEditModalSaved = useCallback(
    async (
      savedConfig: UpsertBuyerInventoryConfigurationMutation['upsertBuyerInventoryConfiguration']
    ) => {
      // Cancel any in-flight refetches so a stale response can't overwrite the
      // values we're about to apply via updateItem.
      await queryClient.cancelQueries({ queryKey: ['buyerInventories'] });
      if (editingInventory) {
        updateItem(editingInventory.id, (item) => ({
          ...item,
          buyerInventoryConfiguration:
            item.buyerInventoryConfiguration && savedConfig
              ? {
                  ...item.buyerInventoryConfiguration,
                  usageRatePerWeek: savedConfig.usageRatePerWeek,
                  weeksCoveredBeforeUrgent:
                    savedConfig.weeksCoveredBeforeUrgent,
                  weeksCoveredBeforeMonitor:
                    savedConfig.weeksCoveredBeforeMonitor,
                  targetWeeksCovered: savedConfig.targetWeeksCovered,
                }
              : item.buyerInventoryConfiguration,
        }));
      }
      void queryClient.invalidateQueries({ queryKey: ['buyerInventories'] });
    },
    [editingInventory, updateItem, queryClient]
  );

  const handleTableEditSubmit = (
    rowId: string,
    columnKey: string,
    newValue: string | number
  ): void => {
    const inventory = allInventories.find((i) => i.id === rowId);
    if (!inventory) return;

    const platformProductsLengthId = inventory.platformProductsLength.id;
    const locationId = inventory.location.id;
    if (!platformProductsLengthId || !locationId) return;

    const unit =
      inventory.platformProductsLength.platformProduct.standardUnitsCode;

    if (columnKey === 'stock') {
      upsertInventory.mutate(
        {
          platformProductsLengthId,
          locationId,
          quantity: {
            value: Number(newValue),
            unit,
          },
        },
        {
          onSuccess: async (result) => {
            // Cancel any in-flight refetches (e.g. from a prior addInventory call)
            // so a stale response can't overwrite the value we're about to apply.
            await queryClient.cancelQueries({ queryKey: ['buyerInventories'] });
            const upserted = result?.upsertInventory;
            const editedStock = { value: Number(newValue), unit };
            updateItem(rowId, (item) => ({
              ...item,
              quantity: upserted?.quantity ?? editedStock,
              availableStock: upserted?.availableStock ?? editedStock,
            }));
            tableControls?.handleEditDone?.();
          },
        }
      );
      return;
    }

    if (columnKey === 'usageRatePerWeek') {
      upsertInventoryConfiguration.mutate(
        {
          platformProductsLengthId,
          locationId,
          usageRatePerWeek: {
            value: Number(newValue),
            unit,
          },
        },
        {
          onSuccess: async () => {
            await queryClient.cancelQueries({ queryKey: ['buyerInventories'] });
            updateItem(rowId, (item) => ({
              ...item,
              buyerInventoryConfiguration: item.buyerInventoryConfiguration
                ? {
                    ...item.buyerInventoryConfiguration,
                    usageRatePerWeek: {
                      value: Number(newValue),
                      unit,
                    },
                  }
                : item.buyerInventoryConfiguration,
            }));
            tableControls?.handleEditDone?.();
          },
        }
      );
    }
  };

  const tableControls = useTable({
    onSort,
    onEditSubmit: handleTableEditSubmit,
  });

  const { t: tGeneral } = useTranslation('general');
  const { data: preferencesData } = useGetUserPreferences();
  const { mutate: updatePreferences } = useUpdateUserPreferences();
  const { order, handleColumnReorderEnd } = useColumnOrderPreference(
    preferencesData?.userPreferences?.buyerInventoryColumnOrder,
    (newOrder, { onError, onSuccess }) =>
      updatePreferences(
        { preferences: { buyerInventoryColumnOrder: newOrder } },
        {
          onSuccess,
          onError: () => {
            onError();
            toast.error(tGeneral('table.reorder_save_error'));
          },
        }
      )
  );
  const columns = useMemo(
    () => applyColumnOrder(buildFlatColumns(t), order),
    [t, order]
  );

  const openEdit = useCallback((inv: Inventory) => {
    setEditingInventory(inv);
    setShowEditInventory(true);
  }, []);

  const rows = useMemo(() => {
    const context = { t, navigate };
    return allInventories.map((inv, idx) =>
      buildInventoryRow(inv, context, {
        rowIndex: idx,
        onOpenEdit: openEdit,
        onAddToBuyList: setAddToBuyListInventory,
      })
    );
  }, [allInventories, openEdit, t, navigate]);

  let tableName = t('inventory.items');
  if (isLoading) tableName = t('inventory.loading');
  else if (error) tableName = t('inventory.load_failed');

  return (
    <div className="overflow-x-auto">
      <Table
        minWidth={60}
        rows={rows}
        columns={columns}
        tableControls={tableControls}
        totalRowsCount={pagination?.totalElements ?? 0}
        name={tableName}
        enableColumnReorder
        onColumnReorderEnd={handleColumnReorderEnd}
        striped
      />
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">{t('inventory.loading')}</div>
        </div>
      )}
      {hasMoreData === false && !isLoading && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">
            {t('inventory.no_more_items')}
          </div>
        </div>
      )}

      <div
        ref={sentinelRef}
        id="scroll-sentinel"
        style={{ height: '1px', background: 'transparent' }}
        data-testid="scroll-sentinel"
      />

      <Modal
        isOpen={showEditInventory}
        onClose={() => {
          setShowEditInventory(false);
          setEditingInventory(null);
        }}
      >
        {editingInventory && (
          <EditInventoryModal
            setShowEditInventory={setShowEditInventory}
            inventory={editingInventory}
            onSaved={handleEditModalSaved}
          />
        )}
      </Modal>

      {addToBuyListInventory && (
        <AddToBuyListModal
          onClose={() => setAddToBuyListInventory(null)}
          location={addToBuyListInventory.location}
          platformProductsLength={addToBuyListInventory.platformProductsLength}
        />
      )}
    </div>
  );
}
